(() => {
  const elementName = 'athos-category-carousels';
  if (customElements.get(elementName)) return;

  const facetValueLabel = (value) => {
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    return window.AthosClient.decodeHtmlEntities(value);
  };

  /* jam-select sort strategy: order options by Athos facet count, highest first. */
  const facetCountOrder = (items, { counts, threshold, hideMinor, current }) => {
    const options = items.filter((item) => item.type === 'option');
    const countOf = (option) => counts.get(option.value) || 0;
    const byCount = (a, b) => countOf(b) - countOf(a) || a.label.localeCompare(b.label, undefined, { numeric: true });
    if (!threshold) return options.slice().sort(byCount);

    const isMajor = (option) => countOf(option) >= threshold || option.value === current;
    const major = options.filter(isMajor).sort(byCount);
    const minor = hideMinor ? [] : options.filter((option) => !isMajor(option)).sort(byCount);
    return minor.length ? [...major, { type: 'group', label: 'More' }, ...minor] : major;
  };

  const toRowDefinition = (node) => ({
    title: node.title,
    handle: node.handle,
    description: node.description,
    url: node.onlineStoreUrl || `/collections/${node.handle}`,
  });

  const buildSkeletonCard = () => {
    const skeleton = document.createElement('li');
    skeleton.className = 'athos-category-card athos-category-card--skeleton';
    skeleton.dataset.athosSkeleton = '';
    skeleton.setAttribute('aria-hidden', 'true');
    skeleton.append(...[
      'athos-category-card__media',
      'athos-category-card__line athos-category-card__line--short',
      'athos-category-card__line',
    ].map((className) => Object.assign(document.createElement('span'), { className })));
    return skeleton;
  };

  class AthosCategoryCarousels extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!window.AthosClient || !window.AthosCards) {
        console.warn('[athos-category-carousels] AthosClient / AthosCards is not loaded.');
        return;
      }
      if (!this.dataset.siteId) {
        console.error('[athos-category-carousels] Missing Athos site ID. Set it in Theme settings → Athos / Searchspring → “Athos site ID”.');
        return;
      }

      this.initialized = true;
      this.lifecycleController = new AbortController();
      this.requestController = new AbortController();
      this.requestGeneration = 0;
      this.athos = window.AthosClient.createClient({
        siteId: this.dataset.siteId,
        ajaxCatalog: this.dataset.ajaxCatalog,
      });
      this.controlsGroup = (this.dataset.controlsGroup || '').trim();
      this.filters = Array.from(this.querySelectorAll('[data-athos-filter]'));
      this.facetCounts = new Map();
      this.sort = this.querySelector('[data-athos-sort]');
      this.clearButton = this.querySelector('[data-athos-clear]');
      this.toolbar = this.querySelector('[data-athos-toolbar]');
      this.announcer = this.querySelector('[data-athos-announcer]');
      this.globalEmpty = this.querySelector('[data-athos-global-empty]');
      this.seeking = this.querySelector('[data-athos-seeking]');
      this.rowsContainer = this.querySelector('[data-athos-rows]');
      this.rowTemplate = this.querySelector('template[data-athos-row-template]');
      this.rows = Array.from(this.querySelectorAll('[data-athos-row]'));
      this.rowStream = this.createRowStreamState();
      this.rowStates = new WeakMap();
      this.initialRows = Math.max(1, Number.parseInt(this.dataset.initialRows || '3', 10));
      this.productsPerPage = Math.max(1, Number.parseInt(this.dataset.productsPerPage || '12', 10));
      this.showResultCount = this.dataset.showResultCount === 'true';
      this.hideEmptyRows = this.dataset.hideEmptyRows === 'true';
      this.isRtl = document.documentElement.dir === 'rtl';

      /* Bursting a request per row trips Athos rate limiting. */
      this.limitFetch = window.AsyncLimiter
        ? window.AsyncLimiter.createLimiter(6)
        : (task) => task();

      this.restoreUrlState();
      this.bindToolbar();
      this.bindSharedControls();
      this.rows.forEach((row) => this.initializeRow(row));
      this.bindThemeEditor();
      this.startLazyLoading();
      this.setupRowStreaming();
      this.attachFacetSort();
      this.primeAutoFilters();
    }

    /* Inject the popularity sort strategy into every filter with data-athos-order="popularity". */
    attachFacetSort() {
      this.filters
        .filter((select) => select.dataset.athosOrder === 'popularity')
        .forEach((select) => {
          const host = select.closest('jam-select');
          if (!host) return;
          const field = select.dataset.filterField;
          const threshold = Number.parseInt(select.dataset.threshold || '0', 10) || 0;
          const hideMinor = 'hideMinor' in select.dataset;
          host.sortFn = (items) => facetCountOrder(items, {
            counts: this.facetCounts.get(field) || new Map(),
            threshold,
            hideMinor,
            current: select.value,
          });
        });
    }

    /* One broad upfront request populates every auto filter at load; per-row facets merge in after. */
    async primeAutoFilters() {
      const query = (this.dataset.facetQuery || '').trim();
      if (!query) return;
      if (!this.filters.some((filter) => 'athosAutoFilter' in filter.dataset)) return;

      try {
        const data = await this.limitFetch(() => this.athos.fetchCategoryPage(
          this.categoryParams(query, 1, 0),
          { signal: this.lifecycleController.signal },
        ));
        this.updateAutoFilters(data?.facets);
      } catch (_error) {
        /* Row responses keep populating options if the priming request fails. */
      }
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
      this.requestController?.abort();
      window.clearTimeout(this.rowStream?.retryTimer);
      this.rowObserver?.disconnect();
      this.sentinelObserver?.disconnect();
      this.initialized = false;
    }

    createRowStreamState() {
      const total = Number.parseInt(this.dataset.rowsTotal || '', 10);
      const streamable = Number.isFinite(total) && total > this.rows.length;
      if (this.dataset.rowsSource === 'metaobject' && streamable && !this.dataset.rowsToken) {
        console.error('[athos-category-carousels] Missing Storefront API token — additional category rows cannot stream. Set it in Theme settings → Athos / Searchspring → “Storefront API token (headless)”.');
      }
      const enabled = this.dataset.rowsSource === 'metaobject'
        && Boolean(this.rowTemplate && this.rowsContainer && this.dataset.rowsToken)
        && Boolean(window.MetaobjectStream);
      return {
        enabled,
        total: Number.isFinite(total) ? total : this.rows.length,
        handles: new Set(this.rows.map((row) => row.dataset.rowHandle).filter(Boolean)),
        /* The rows list caps at 128 references, so the order continues in rows_2. */
        iterator: enabled
          ? window.MetaobjectStream.streamReferences({
            token: this.dataset.rowsToken,
            metaobject: {
              type: this.dataset.rowsMetaobjectType || 'by_size_categories',
              handle: this.dataset.rowsMetaobjectHandle || 'by-size',
            },
            pageSize: 12,
            nodeFragment: '... on Collection { title handle description(truncateAt: 200) onlineStoreUrl }',
          })
          : undefined,
        pending: null,
        walking: false,
        done: false,
        retryTimer: null,
        retryAttempts: 0,
      };
    }

    setupRowStreaming() {
      const stream = this.rowStream;
      if (!stream.enabled || this.rows.length >= stream.total) return;

      this.rowSentinel = document.createElement('div');
      this.rowSentinel.setAttribute('data-athos-row-sentinel', '');
      /* theme.css hides div:empty, which would blind the IntersectionObserver. */
      this.rowSentinel.style.cssText = 'display:block;width:100%;height:1px;';
      this.rowsContainer.append(this.rowSentinel);

      if (!('IntersectionObserver' in window)) {
        this.ensureRows(stream.total);
        return;
      }

      this.sentinelObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.fetchMoreRows();
      }, { rootMargin: '2400px 0px' });
      this.sentinelObserver.observe(this.rowSentinel);

      const depth = this.getUrlDepth();
      if (depth > this.rows.length) this.ensureRows(depth);
    }

    fetchMoreRows() {
      const stream = this.rowStream;
      if (!stream.enabled || stream.done || stream.pending) return stream.pending || Promise.resolve();

      const request = stream.iterator.next()
        .then(({ done, value }) => {
          if (done) {
            this.finishRowStreaming();
            return;
          }
          if (!value.ok) {
            console.warn('[athos-category-carousels] row fetch failed', value.error);
            this.scheduleRowRetry();
            return;
          }
          window.clearTimeout(stream.retryTimer);
          stream.retryTimer = null;
          stream.retryAttempts = 0;
          value.nodes
            .map(toRowDefinition)
            .filter((definition) => definition.handle && !stream.handles.has(definition.handle))
            .forEach((definition) => this.stampRow(definition));
        })
        .catch((error) => {
          console.warn('[athos-category-carousels] row fetch failed', error);
          this.scheduleRowRetry();
        })
        .finally(() => {
          if (stream.pending === request) stream.pending = null;
        });

      stream.pending = request;
      return request;
    }

    scheduleRowRetry() {
      const stream = this.rowStream;
      if (!stream.enabled || stream.done || stream.retryTimer || stream.retryAttempts >= 3) return;

      const delay = Math.min(1000 * (2 ** stream.retryAttempts), 4000);
      stream.retryAttempts += 1;
      stream.retryTimer = window.setTimeout(() => {
        stream.retryTimer = null;
        this.fetchMoreRows();
      }, delay);
    }

    stampRow({ title, url, description, handle }) {
      this.rowStream.handles.add(handle);

      const fragment = this.rowTemplate.content.cloneNode(true);
      const row = fragment.querySelector('[data-athos-row]');
      if (!row) return;

      row.dataset.rowIndex = String(this.rows.length);
      row.dataset.rowHandle = handle;
      row.dataset.query = `bgfilter.collection_handle=${handle}`;

      const link = row.querySelector('[data-athos-row-link]');
      if (link && url) link.href = url;
      const titleText = row.querySelector('[data-athos-row-title-text]');
      if (titleText) titleText.textContent = title || handle;
      const descriptionText = row.querySelector('[data-athos-row-description]');
      if (descriptionText && description) {
        descriptionText.textContent = description;
        descriptionText.hidden = false;
      }
      row.querySelector('[data-athos-previous]')?.setAttribute('aria-label', `Previous products in ${title}`);
      row.querySelector('[data-athos-next]')?.setAttribute('aria-label', `Next products in ${title}`);

      const seekMode = this.filtersActive();
      if (seekMode) row.hidden = true;
      this.rowsContainer.insertBefore(fragment, this.rowSentinel);
      this.rows.push(row);
      this.initializeRow(row);
      if (seekMode || !this.rowObserver) {
        this.loadRow(row);
      } else {
        this.rowObserver.observe(row);
      }
    }

    finishRowStreaming() {
      this.rowStream.done = true;
      window.clearTimeout(this.rowStream.retryTimer);
      this.rowStream.retryTimer = null;
      this.sentinelObserver?.disconnect();
      this.rowSentinel?.remove();
      this.updateSeeking();
      this.updateGlobalEmptyState();
    }

    async ensureRows(count) {
      const stream = this.rowStream;
      const target = Math.min(count, stream.total);
      /* Bounded so a failing endpoint cannot loop forever. */
      for (let attempt = 0; attempt < 40 && this.rows.length < target && !stream.done; attempt += 1) {
        await this.fetchMoreRows();
      }
      this.rows.slice(0, target).forEach((row) => this.loadRow(row));
    }

    bindToolbar() {
      if (!this.toolbar) return;

      const signal = this.lifecycleController.signal;
      this.toolbar.addEventListener('submit', (event) => event.preventDefault(), { signal });
      this.toolbar.addEventListener('change', (event) => {
        if (!event.target.matches('[data-athos-filter], [data-athos-sort]')) return;
        this.handleControlsChange();
      }, { signal });

      this.clearButton?.addEventListener('click', () => {
        this.filters.forEach((filter) => {
          filter.value = '';
          delete filter.dataset.restoreValue;
        });
        this.handleControlsChange();
      }, { signal });

      this.updateClearButton();
    }

    bindSharedControls() {
      if (!this.controlsGroup) return;

      document.addEventListener('athos:controls-change', (event) => {
        if (event.detail?.source === this || event.detail?.group !== this.controlsGroup) return;

        this.applyControlsState(event.detail.state);
        this.updateClearButton();
        this.announce('Updating products.');
        this.resetRows();
      }, { signal: this.lifecycleController.signal });
    }

    getControlsState() {
      const selectedSort = this.sort?.selectedOptions?.[0];

      return {
        filters: this.filters.map((filter) => ({
          field: filter.dataset.filterField,
          value: this.getFilterValue(filter),
        })),
        sort: selectedSort?.dataset.sortField ? {
          field: selectedSort.dataset.sortField,
          direction: selectedSort.dataset.sortDirection || 'asc',
        } : undefined,
      };
    }

    getFilterValue(filter) {
      return filter.value || filter.dataset.restoreValue || '';
    }

    applyControlsState(state) {
      const valuesByField = new Map(
        (state?.filters || []).map(({ field, value }) => [field, value]),
      );

      this.filters.forEach((filter) => {
        const value = valuesByField.get(filter.dataset.filterField) || '';
        filter.value = Array.from(filter.options).some((option) => option.value === value)
          ? value
          : '';
      });

      if (!this.sort || !state?.sort) return;
      const option = Array.from(this.sort.options).find((candidate) => (
        candidate.dataset.sortField === state.sort.field
        && (candidate.dataset.sortDirection || 'asc') === state.sort.direction
      ));
      if (option) this.sort.value = option.value;
    }

    broadcastControlsState() {
      if (!this.controlsGroup) return;

      document.dispatchEvent(new CustomEvent('athos:controls-change', {
        detail: {
          source: this,
          group: this.controlsGroup,
          state: this.getControlsState(),
        },
      }));
    }

    bindThemeEditor() {
      document.addEventListener('shopify:block:select', (event) => {
        const row = event.target?.closest?.('[data-athos-row]');
        if (!row || !this.contains(row)) return;

        row.hidden = false;
        this.loadRow(row);
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, { signal: this.lifecycleController.signal });
    }

    initializeRow(row) {
      const viewport = row.querySelector('[data-athos-viewport]');
      const previousButton = row.querySelector('[data-athos-previous]');
      const nextButton = row.querySelector('[data-athos-next]');
      const retryButton = row.querySelector('[data-athos-retry]');
      const state = this.createRowState(row);

      this.rowStates.set(row, state);

      viewport?.addEventListener('scroll', () => this.handleRowScroll(row), {
        passive: true,
        signal: this.lifecycleController.signal,
      });

      previousButton?.addEventListener('click', () => {
        this.scrollRow(row, -1);
      }, { signal: this.lifecycleController.signal });

      nextButton?.addEventListener('click', async () => {
        const currentState = this.rowStates.get(row);
        if (this.isAtRowEnd(row) && currentState.page < currentState.totalPages) {
          await this.loadNextPage(row);
        }
        this.scrollRow(row, 1);
      }, { signal: this.lifecycleController.signal });

      retryButton?.addEventListener('click', () => {
        const currentState = this.rowStates.get(row);
        currentState.errored = false;
        this.hideRowMessage(row);
        row.hidden = false;
        row.setAttribute('aria-busy', 'true');
        if (currentState.page === 0) this.renderSkeletons(row);
        this.loadNextPage(row);
      }, { signal: this.lifecycleController.signal });
    }

    createRowState(row) {
      return {
        row,
        page: 0,
        totalPages: Number.POSITIVE_INFINITY,
        totalResults: undefined,
        pending: null,
        loaded: false,
        errored: false,
        productIds: new Set(),
        scrollFrame: null,
        generation: this.requestGeneration,
      };
    }

    startLazyLoading() {
      this.rowObserver?.disconnect();
      const urlDepth = this.getUrlDepth();
      const initialCount = Math.min(
        this.rows.length,
        Math.max(this.initialRows, urlDepth),
      );

      if ('IntersectionObserver' in window) {
        this.rowObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.loadRow(entry.target);
          });
        }, { rootMargin: '2000px 0px' });

        this.rows.slice(initialCount).forEach((row) => this.rowObserver.observe(row));
      }

      const generation = this.requestGeneration;
      const initialRequests = this.rows
        .slice(0, initialCount)
        .map((row) => this.loadRow(row));

      if (!('IntersectionObserver' in window)) {
        this.rows.slice(initialCount).forEach((row) => initialRequests.push(this.loadRow(row)));
      }

      Promise.allSettled(initialRequests).then(() => {
        if (generation === this.requestGeneration) this.announce('Products updated.');
      });
    }

    loadRow(row) {
      const state = this.rowStates.get(row);
      if (!state) return Promise.resolve();

      this.rowObserver?.unobserve(row);
      this.updateUrlDepth(Number.parseInt(row.dataset.rowIndex || '0', 10) + 1);
      if (state.loaded || state.pending) return state.pending || Promise.resolve();

      state.loaded = true;
      return this.loadNextPage(row);
    }

    async loadNextPage(row) {
      const state = this.rowStates.get(row);
      if (!state || state.pending || state.page >= state.totalPages) return state?.pending;

      const requestedPage = state.page + 1;
      const generation = this.requestGeneration;
      const signal = this.requestController.signal;
      const params = this.categoryParams(row.dataset.query, requestedPage);
      const request = this.limitFetch(() => this.athos.fetchCategoryPage(params, { signal }))
        .then((data) => {
          if (generation !== this.requestGeneration) return;
          this.renderPage(row, data, requestedPage, { generation, signal });
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || generation !== this.requestGeneration) return;
          this.renderError(row);
        })
        .finally(() => {
          if (state.pending === request) state.pending = null;
          if (generation === this.requestGeneration) {
            row.setAttribute('aria-busy', 'false');
            this.updateRowControls(row);
          }
        });

      state.pending = request;
      row.setAttribute('aria-busy', 'true');
      this.updateRowControls(row);
      return request;
    }

    categoryParams(rowQuery, page, resultsPerPage = this.productsPerPage) {
      const selectedSort = this.sort?.selectedOptions?.[0];
      return {
        rowQuery,
        globalQuery: this.dataset.globalQuery,
        collectionId: this.dataset.shopifyCollectionId,
        collectionHandle: this.dataset.shopifyCollectionHandle,
        page,
        resultsPerPage,
        filters: this.filters.map((filter) => ({
          field: filter.dataset.filterField,
          value: this.getFilterValue(filter),
        })),
        sort: selectedSort?.dataset.sortField
          ? { field: selectedSort.dataset.sortField, direction: selectedSort.dataset.sortDirection }
          : undefined,
      };
    }

    /* Accumulate facet counts across responses, keeping the max. Returns the changed fields. */
    collectFacetCounts(facets) {
      const changed = new Set();
      facets.forEach((facet) => {
        if (!facet?.field) return;
        let known = this.facetCounts.get(facet.field);
        if (!known) {
          known = new Map();
          this.facetCounts.set(facet.field, known);
        }
        (facet.values || []).forEach((entry) => {
          const value = String(entry?.value ?? '').trim();
          if (!value) return;
          const count = Number(entry?.count) || 0;
          const existing = known.get(value);
          if (existing === undefined || count > existing) {
            known.set(value, count);
            changed.add(facet.field);
          }
        });
      });
      return changed;
    }

    /* Feed changed counts back: catalog filters re-render options, popularity filters re-sort. */
    updateAutoFilters(facets) {
      if (!Array.isArray(facets)) return;
      const changedFields = this.collectFacetCounts(facets);
      if (!changedFields.size) return;

      this.filters.forEach((select) => {
        const field = select.dataset.filterField;
        if (!changedFields.has(field)) return;
        if ('athosAutoFilter' in select.dataset) {
          this.renderAutoFilterOptions(select, this.facetCounts.get(field));
        } else if (select.dataset.athosOrder === 'popularity') {
          select.closest('jam-select')?.refresh();
        }
      });
    }

    /* Materialise known values as flat options for the hidden native select. */
    renderAutoFilterOptions(select, valueCounts) {
      const desired = select.value || select.dataset.restoreValue || '';
      const placeholder = select.options[0];

      const options = Array.from(valueCounts.keys())
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = facetValueLabel(value);
          return option;
        });
      select.replaceChildren(placeholder, ...options);

      if (desired && Array.from(select.options).some((option) => option.value === desired)) {
        select.value = desired;
        if (select.dataset.restoreValue) {
          /* A deep-linked value arrived before its option; apply it now. */
          delete select.dataset.restoreValue;
          this.handleControlsChange();
        }
      }
      select.closest('jam-select')?.refresh();
    }

    renderPage(row, data, page, request = {}) {
      this.updateAutoFilters(data?.facets);
      const state = this.rowStates.get(row);
      const results = Array.isArray(data?.results) ? data.results : [];
      const pagination = data?.pagination || {};
      const totalResults = Number.parseInt(pagination.totalResults ?? results.length, 10) || 0;
      const totalPages = Number.parseInt(
        pagination.totalPages ?? Math.ceil(totalResults / this.productsPerPage),
        10,
      ) || 0;

      if (page === 1) this.removeSkeletons(row);

      state.page = page;
      state.totalResults = totalResults;
      state.totalPages = totalPages;

      if (page === 1 && results.length === 0) {
        this.renderEmpty(row);
        return;
      }

      const track = row.querySelector('[data-athos-track]');
      const handles = [];
      results.forEach((result, index) => {
        const product = window.AthosClient.normalizeProduct(result);
        const productId = product.id || `${page}-${index}-${product.url}`;
        if (state.productIds.has(productId)) return;

        state.productIds.add(productId);
        const handle = window.AthosClient.handleFromResult(result);
        if (handle) handles.push(handle);
      });
      this.appendCards(track, handles, request);

      if (row.hidden && this.filtersActive()) row.classList.add('athos-category-row--reveal');
      row.hidden = false;
      this.hideRowMessage(row);
      this.updateGlobalEmptyState();
      this.updateSeeking();

      const count = row.querySelector('[data-athos-count]');
      if (count && this.showResultCount) {
        /* Products only. Athos folds banner slots into totalResults; productResults strips them. */
        const productTotal = pagination.productResults ?? results.length;
        count.textContent = `${productTotal.toLocaleString()} ${productTotal === 1 ? 'product' : 'products'}`;
        count.hidden = false;
      }
    }

    /* Fetch jam-card markup via ?view=card and wrap each in the carousel's <li>. */
    async appendCards(track, handles, { generation, signal } = {}) {
      if (!track || !handles.length) return;
      const cardSignal = signal || this.lifecycleController?.signal;
      const cards = await window.AthosCards.fetchCards(handles, { signal: cardSignal });
      const stale = cardSignal?.aborted
        || this.lifecycleController?.signal.aborted
        || (generation !== undefined && generation !== this.requestGeneration);
      if (stale) return;
      cards.forEach((html) => {
        if (html) track.insertAdjacentHTML('beforeend', `<li class="athos-category-card">${html}</li>`);
      });
      window.DisplayGroupSwatches?.register(track);
    }

    renderEmpty(row) {
      this.removeSkeletons(row);
      row.setAttribute('aria-busy', 'false');

      if (this.hideEmptyRows && !window.Shopify?.designMode) {
        row.hidden = true;
      } else {
        this.showRowMessage(row, this.dataset.emptyMessage || 'No products match these filters.', false);
      }
      this.updateGlobalEmptyState();
      this.updateSeeking();
      this.updateRowControls(row);
    }

    renderError(row) {
      /* Errored rows count as resolved so the seek indicator can settle. */
      const state = this.rowStates.get(row);
      if (state) state.errored = true;
      this.removeSkeletons(row);
      row.hidden = false;
      row.setAttribute('aria-busy', 'false');
      this.showRowMessage(row, this.dataset.errorMessage || 'Products could not be loaded.', true);
      this.updateSeeking();
      this.updateGlobalEmptyState();
    }

    showRowMessage(row, message, showRetry) {
      const container = row.querySelector('[data-athos-message]');
      const text = row.querySelector('[data-athos-message-text]');
      const retry = row.querySelector('[data-athos-retry]');
      if (!container || !text || !retry) return;

      text.textContent = message;
      retry.hidden = !showRetry;
      container.hidden = false;
    }

    hideRowMessage(row) {
      const message = row.querySelector('[data-athos-message]');
      if (message) message.hidden = true;
    }

    renderSkeletons(row) {
      const track = row.querySelector('[data-athos-track]');
      track?.replaceChildren(...Array.from({ length: 4 }, buildSkeletonCard));
    }

    removeSkeletons(row) {
      row.querySelectorAll('[data-athos-skeleton]').forEach((skeleton) => skeleton.remove());
    }

    handleRowScroll(row) {
      const state = this.rowStates.get(row);
      if (!state || state.scrollFrame) return;

      state.scrollFrame = window.requestAnimationFrame(() => {
        state.scrollFrame = null;
        this.updateRowControls(row);

        if (this.getScrollProgress(row) > 0.45 && state.page < state.totalPages) {
          this.loadNextPage(row);
        }
      });
    }

    scrollRow(row, direction) {
      const viewport = row.querySelector('[data-athos-viewport]');
      if (!viewport) return;

      const distance = Math.max(240, viewport.clientWidth * 0.9) * direction * (this.isRtl ? -1 : 1);
      viewport.scrollBy({ left: distance, behavior: 'smooth' });
    }

    getScrollPosition(row) {
      const viewport = row.querySelector('[data-athos-viewport]');
      if (!viewport) return 0;
      if (!this.isRtl) return viewport.scrollLeft;
      if (viewport.scrollLeft < 0) return -viewport.scrollLeft;
      return viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft;
    }

    getScrollProgress(row) {
      const viewport = row.querySelector('[data-athos-viewport]');
      if (!viewport) return 0;
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      return maximum > 0 ? this.getScrollPosition(row) / maximum : 1;
    }

    isAtRowEnd(row) {
      return this.getScrollProgress(row) > 0.97;
    }

    updateRowControls(row) {
      const state = this.rowStates.get(row);
      const previousButton = row.querySelector('[data-athos-previous]');
      const nextButton = row.querySelector('[data-athos-next]');
      const viewport = row.querySelector('[data-athos-viewport]');
      if (!state || !previousButton || !nextButton || !viewport) return;

      const hasOverflow = viewport.scrollWidth - viewport.clientWidth > 4;
      const hasMorePages = state.page < state.totalPages;
      previousButton.disabled = !hasOverflow || this.getScrollPosition(row) < 4;
      nextButton.disabled = Boolean(state.pending) || (!hasMorePages && (!hasOverflow || this.isAtRowEnd(row)));
    }

    handleControlsChange() {
      this.updateClearButton();
      this.syncUrlState();
      this.announce('Updating products.');
      this.broadcastControlsState();
      /* Paint the changed control before the reset works the DOM. */
      window.requestAnimationFrame(() => {
        window.setTimeout(() => this.resetRows(), 0);
      });
    }

    resetRows() {
      this.requestController.abort();
      this.requestController = new AbortController();
      this.requestGeneration += 1;
      this.rowObserver?.disconnect();
      if (this.globalEmpty) this.globalEmpty.hidden = true;

      const seekMode = this.filtersActive();
      this.rows.forEach((row) => {
        const oldState = this.rowStates.get(row);
        if (oldState?.scrollFrame) window.cancelAnimationFrame(oldState.scrollFrame);

        row.classList.remove('athos-category-row--reveal');
        row.hidden = seekMode;
        row.setAttribute('aria-busy', 'true');
        const viewport = row.querySelector('[data-athos-viewport]');
        if (viewport) viewport.scrollLeft = 0;
        const count = row.querySelector('[data-athos-count]');
        if (count) count.hidden = true;
        this.hideRowMessage(row);
        this.rowStates.set(row, this.createRowState(row));
        if (seekMode) {
          /* Hidden rows only need their stale cards cleared. */
          row.querySelector('[data-athos-track]')?.replaceChildren();
        } else {
          this.renderSkeletons(row);
          this.updateRowControls(row);
        }
      });

      this.clearUrlDepth();
      if (seekMode) {
        /* Hiding every row collapses the page; keep scroll pinned here. */
        this.scrollIntoView({ behavior: 'auto', block: 'start' });
        this.rows.forEach((row) => this.loadRow(row));
        this.streamAllRows();
        this.updateSeeking();
      } else {
        this.startLazyLoading();
        this.updateSeeking();
      }
    }

    updateGlobalEmptyState() {
      if (!this.globalEmpty || this.rows.length === 0) return;

      /* Only conclude "nothing matches" once streaming and resolution settle. */
      const states = this.rows.map((row) => this.rowStates.get(row));
      const streaming = this.rowStream.enabled && !this.rowStream.done;
      const unresolved = states.some((state) => state?.loaded && !state.errored && state.totalResults === undefined);
      const errored = states.some((state) => state?.errored);
      this.globalEmpty.hidden = streaming || unresolved || errored
        || !states.every((state) => state?.totalResults === 0);
    }

    updateClearButton() {
      if (this.clearButton) {
        this.clearButton.hidden = !this.filtersActive();
      }
    }

    filtersActive() {
      return this.filters.some((filter) => Boolean(this.getFilterValue(filter)));
    }

    /* Seek mode: with filters active, rows stay hidden until a response proves a match. */
    updateSeeking() {
      if (!this.seeking) return;
      if (!this.filtersActive()) {
        this.seeking.hidden = true;
        return;
      }
      const unresolved = this.rows.some((row) => {
        const state = this.rowStates.get(row);
        return state?.loaded && !state.errored && state.totalResults === undefined;
      });
      this.seeking.hidden = !(unresolved || this.rowStream.walking);
    }

    /* Hidden rows never trigger the sentinel, so walk the remaining list explicitly. */
    async streamAllRows() {
      const stream = this.rowStream;
      if (stream.walking) return;
      stream.walking = true;

      const maxPulls = Math.ceil(stream.total / 12) + 10;
      let stalls = 0;
      for (let pull = 0; pull < maxPulls && stream.enabled && !stream.done && stalls < 5; pull += 1) {
        const before = this.rows.length;
        await this.fetchMoreRows();
        stalls = this.rows.length > before || stream.done ? 0 : stalls + 1;
      }

      stream.walking = false;
      this.updateSeeking();
      this.updateGlobalEmptyState();
    }

    restoreUrlState() {
      const params = new URL(window.location.href).searchParams;

      this.filters.forEach((filter) => {
        const value = params.get(`clf.${filter.dataset.filterField}`);
        if (!value) return;
        if (Array.from(filter.options).some((option) => option.value === value)) {
          filter.value = value;
        } else if ('athosAutoFilter' in filter.dataset) {
          filter.dataset.restoreValue = value;
        }
      });

      if (this.sort) {
        const field = params.get('cls.sort');
        const direction = params.get('cls.direction') || 'asc';
        const option = Array.from(this.sort.options).find((candidate) => (
          candidate.dataset.sortField === field
          && candidate.dataset.sortDirection === direction
        ));
        if (option) this.sort.value = option.value;
      }
    }

    syncUrlState() {
      const url = new URL(window.location.href);
      Array.from(new Set(url.searchParams.keys())).forEach((key) => {
        if (key.startsWith('clf.')) url.searchParams.delete(key);
      });

      this.filters.forEach((filter) => {
        const value = this.getFilterValue(filter);
        if (value) url.searchParams.set(`clf.${filter.dataset.filterField}`, value);
      });

      const selectedSort = this.sort?.selectedOptions?.[0];
      if (selectedSort?.dataset.sortField) {
        url.searchParams.set('cls.sort', selectedSort.dataset.sortField);
        url.searchParams.set('cls.direction', selectedSort.dataset.sortDirection || 'asc');
      }

      window.history.replaceState(null, '', url);
    }

    getUrlDepth() {
      const depth = Number.parseInt(new URL(window.location.href).searchParams.get('depth') || '0', 10);
      return Number.isFinite(depth) ? depth : 0;
    }

    updateUrlDepth(depth) {
      /* Depth restore is an unfiltered-browsing affordance. */
      if (this.filtersActive()) return;
      if (depth <= this.initialRows || depth <= this.getUrlDepth()) return;
      const url = new URL(window.location.href);
      url.searchParams.set('depth', String(depth));
      window.history.replaceState(null, '', url);
    }

    clearUrlDepth() {
      const url = new URL(window.location.href);
      url.searchParams.delete('depth');
      window.history.replaceState(null, '', url);
    }

    announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = '';
      window.requestAnimationFrame(() => {
        this.announcer.textContent = message;
      });
    }
  }

  customElements.define(elementName, AthosCategoryCarousels);
})();
